import { DEFAULT_REGION_POLICIES } from '@ca/common'
import type { RegionPolicyConfig } from '@ca/common/types'
import type { CreateCustomerData } from '../schemas'
import {
  getStepSchema,
  pickStepSlice,
  type StepValidationContext,
} from '../stepSchemas'
import { CreateStepKey } from '../types'
import { hasAnyInvoiceData } from './invoice'
import { isKidsAccount } from './isKids'
import { isKidsContactStepValid } from './parentAuth'

export type { StepValidationContext }

export type StepStatus = 'default' | 'success' | 'danger'

export const isInvoiceRequired = (data: Partial<CreateCustomerData>) =>
  isKidsAccount(data)

export const createStepValidationContext = (
  data: Partial<CreateCustomerData>,
  regionPolicies: RegionPolicyConfig[],
  vatVisible: boolean
): StepValidationContext => ({
  regionPolicies,
  invoiceRequiresVat: vatVisible && !isKidsAccount(data),
})

const validateStep = (
  step: CreateStepKey,
  data: Partial<CreateCustomerData>,
  ctx: StepValidationContext
) => getStepSchema(step, ctx).safeParse(pickStepSlice(step, data)).success

const isInvoiceStepValid = (
  data: Partial<CreateCustomerData>,
  ctx: StepValidationContext
) => {
  if (!isInvoiceRequired(data) && !hasAnyInvoiceData(data.invoice)) {
    return true
  }
  return validateStep('invoice', data, ctx)
}

export const hasAnyDataFor = (
  step: CreateStepKey,
  data: Partial<CreateCustomerData>
): boolean => {
  switch (step) {
    case 'personal':
      return !!(data.first_name || data.last_name || data.birthday)
    case 'contact':
      return !!(data.email || data.mobile || data.phone)
    case 'address':
      return !!(
        data.address?.street ||
        data.address?.city ||
        data.address?.zip
      )
    case 'invoice':
      return hasAnyInvoiceData(data.invoice)
    case 'policies':
      return !!data.policies && Object.values(data.policies).some(Boolean)
  }
}

export const isStepValid = (
  step: CreateStepKey,
  data: Partial<CreateCustomerData>,
  ctx: StepValidationContext
): boolean => {
  if (step === 'invoice') {
    return isInvoiceStepValid(data, ctx)
  }
  if (step === 'contact' && isKidsAccount(data) && !isKidsContactStepValid(data)) {
    return false
  }
  return validateStep(step, data, ctx)
}

export const getStepStatus = (
  step: CreateStepKey,
  data: Partial<CreateCustomerData>,
  ctx: StepValidationContext
): StepStatus => {
  if (!hasAnyDataFor(step, data)) {
    return 'default'
  }
  return isStepValid(step, data, ctx) ? 'success' : 'danger'
}

export const getErrorSections = (
  data: Partial<CreateCustomerData>,
  ctx: StepValidationContext = {
    regionPolicies: DEFAULT_REGION_POLICIES,
    invoiceRequiresVat: false,
  }
): Set<CreateStepKey> => {
  const errors = new Set<CreateStepKey>()
  const kids = isKidsAccount(data)

  if (!validateStep('personal', data, ctx)) {
    errors.add('personal')
  }
  if (!isStepValid('contact', data, ctx)) {
    errors.add('contact')
  }
  if (!kids && !validateStep('address', data, ctx)) {
    errors.add('address')
  }
  if (!isInvoiceStepValid(data, ctx)) {
    errors.add('invoice')
  }
  if (!validateStep('policies', data, ctx)) {
    errors.add('policies')
  }
  return errors
}
